import { apiUrl, createApiEndpoint } from './apiConfig';


const GATEWAY_ERRORS = [502, 503, 504];

/**
 * Enhanced fetch with error handling and retries.
 * Each attempt gets its own timeout; client (4xx) and gateway (502/503/504) errors are not retried.
 */
async function apiRequest(endpoint, options = {}) {
  const {
    retries = 3,
    timeout = 10000,
    ...fetchOptions
  } = options;

  for (let attempt = 1; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(apiUrl(endpoint), {
        ...fetchOptions,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
      });

      if (!response.ok) {
        const httpError = new Error(`HTTP ${response.status}: ${response.statusText}`);
        httpError.status = response.status;
        throw httpError;
      }

      return await response.json();

    } catch (error) {
      // Client errors will not change on retry, and a gateway error means the API is down:
      // the caller has the JSON files to fall back on, so fail fast
      const isFinal = (error.status >= 400 && error.status < 500) || GATEWAY_ERRORS.includes(error.status);
      if (attempt === retries || isFinal) {
        throw new Error(`API request failed after ${attempt} attempt${attempt === 1 ? '' : 's'}: ${error.message}`);
      }

      // Short exponential backoff: callers fall back to the JSON files, so a dead API must fail fast
      const delay = 250 * Math.pow(2, attempt - 1);
      console.warn(`API request attempt ${attempt} failed, retrying in ${delay}ms:`, error.message);
      await new Promise(resolve => setTimeout(resolve, delay));
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Get season data (race results or standings)
 * @param {string} dataType - "race" or "standings"
 * @param {number} season - Year
 * @param {number} raceNumber - Optional max race number
 * @param {string} quality - "fantasy", "core", or "full"
 * @returns {Promise<Array>} Season data
 */
export const getSeasonData = async (dataType, season, raceNumber = null, quality = 'core') => {
  const params = new URLSearchParams();
  if (raceNumber) params.append('raceNumber', raceNumber);
  if (quality !== 'core') params.append('quality', quality);
  
  const endpoint = createApiEndpoint(`/api/data/season/${dataType}/${season}?${params.toString()}`);
  const response = await apiRequest(endpoint);
  
  // Handle both old format (array) and new format (object with data property)
  if (Array.isArray(response)) {
    return response;
  } else if (response && response.data) {
    return response.data;
  } else {
    console.warn('Unexpected API response format:', response);
    return [];
  }
};

/**
 * Get multi-season data
 * @param {string} dataType - "race" or "standings"
 * @param {number} startYear - Earliest year to include
 * @param {string} quality - "fantasy", "core", or "full"
 * @returns {Promise<Array>} Multi-season data
 */
export const getManySeasonData = async (dataType, startYear, quality = 'core') => {
  const params = new URLSearchParams();
  params.append('startYear', startYear);
  if (quality !== 'core') params.append('quality', quality);
  
  const endpoint = createApiEndpoint(`/api/data/many-seasons/${dataType}?${params.toString()}`);
  const response = await apiRequest(endpoint);
  
  // Handle both old format (array) and new format (object with data property)
  if (Array.isArray(response)) {
    return response;
  } else if (response && response.data) {
    return response.data;
  } else {
    console.warn('Unexpected API response format:', response);
    return [];
  }
};

/**
 * Get track-specific data across seasons
 * @param {number} startYear - Earliest year to include
 * @param {string} track - Track name
 * @param {string} trackFilterType - "exact", "similar", or "both"
 * @param {string} quality - "fantasy", "core", or "full"
 * @returns {Promise<Array>} Track data
 */
export const getTrackData = async (startYear, track, trackFilterType = 'exact', quality = 'core') => {
  const params = new URLSearchParams();
  params.append('startYear', startYear);
  params.append('filterType', trackFilterType);
  if (quality !== 'core') params.append('quality', quality);
  
  const endpoint = createApiEndpoint(`/api/data/track/${encodeURIComponent(track)}?${params.toString()}`);
  const response = await apiRequest(endpoint);
  
  // Handle both old format (array) and new format (object with data property)
  if (Array.isArray(response)) {
    return response;
  } else if (response && response.data) {
    return response.data;
  } else {
    console.warn('Unexpected API response format:', response);
    return [];
  }
};

/**
 * Load static JSON data (calendar, next race, etc.)
 * @param {string} filename - Name of the JSON file
 * @returns {Promise<any>} Static data
 */
export const loadJsonData = async (filename) => {
  const endpoint = createApiEndpoint(`/api/data/static/${filename}`);
  return await apiRequest(endpoint);
};

const apiClient = {
  getSeasonData,
  getManySeasonData,
  getTrackData,
  loadJsonData
};

export default apiClient;
